import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faHistory, faSpinnerThird, faPlus, faTrash, faChartLine } from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { getBooking } from "../../store/booking/bookingActions";
import { Formik, Form, Field } from "formik";
import SubmitButton from "../Forms/SubmitButton";
import SettingsSwitch from "../Forms/SettingsSwitch";
import QuickFormDate from "../Forms/QuickFormDate";
import QuickFormText from "../Forms/QuickFormText";
import SettingsSelect from "../Forms/SettingsSelect";
import axios from "axios";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

export default function CancellationsModal({ isOpen, onClose, onOpenReport }) {
    const dispatch = useDispatch();
    const { current_booking } = useSelector((state) => state.booking);
    const { current_staff } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [showAddHistory, setShowAddHistory] = useState(false);
    const [addingHistory, setAddingHistory] = useState(false);
    const [newHistoryChoice, setNewHistoryChoice] = useState("");
    const [newHistoryDate, setNewHistoryDate] = useState("");
    const [newHistoryNotes, setNewHistoryNotes] = useState("");
    const [showFieldInfo, setShowFieldInfo] = useState(false);

    const renewalChoiceOptions = [
        { value: "YES, RENEW", label: "YES, RENEW" },
        { value: "REMAIN MONTH 2 MONTH", label: "REMAIN MONTH 2 MONTH" },
        { value: "MONTH 2 MONTH LEAVING", label: "MONTH 2 MONTH LEAVING" },
        { value: "LEAVING, END JAN", label: "LEAVING, END JAN" },
        { value: "LEAVING, END FEB", label: "LEAVING, END FEB" },
        { value: "LEAVING, END MAR", label: "LEAVING, END MAR" },
        { value: "LEAVING, END APR", label: "LEAVING, END APR" },
        { value: "LEAVING, END MAY", label: "LEAVING, END MAY" },
        { value: "LEAVING, END JUN", label: "LEAVING, END JUN" },
        { value: "LEAVING, END JUL", label: "LEAVING, END JUL" },
        { value: "LEAVING, END AUG", label: "LEAVING, END AUG" },
        { value: "LEAVING, END SEP", label: "LEAVING, END SEP" },
        { value: "LEAVING, END OCT", label: "LEAVING, END OCT" },
        { value: "LEAVING, END NOV", label: "LEAVING, END NOV" },
        { value: "LEAVING, END DEC", label: "LEAVING, END DEC" },
        { value: "REVOKE CANCELLATION", label: "REVOKE CANCELLATION" },
    ];

    const cancellationTypeOptions = [
        { value: "", label: "Unspecified" },
        { value: "early_termination", label: "Early Termination" },
        { value: "abscond", label: "Abscond" },
        { value: "eviction", label: "Eviction" },
    ];

    // Fetch renewal history when modal opens
    useEffect(() => {
        if (current_booking?.id && isOpen) {
            fetchHistory();
        }
    }, [current_booking?.id, isOpen]);

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}monthend/history/${current_booking.id}`
            );
            setHistory(response.data.history || []);
        } catch (error) {
            console.error("Failed to fetch renewal history:", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const deriveEndMonth = (choice) => {
        if (!choice) return null;
        const match = choice.match(/LEAVING,?\s*END\s+(\w+)/i);
        if (!match) return null;
        const monthMap = { JAN: 'Jan', FEB: 'Feb', MAR: 'Mar', APR: 'Apr', MAY: 'May', JUN: 'Jun', JUL: 'Jul', AUG: 'Aug', SEP: 'Sep', OCT: 'Oct', NOV: 'Nov', DEC: 'Dec' };
        const abbr = match[1].toUpperCase().substring(0, 3);
        const monthStd = monthMap[abbr] || match[1];
        const now = new Date();
        const monthNum = new Date(monthStd + ' 1, 2000').getMonth();
        const year = monthNum >= now.getMonth() ? now.getFullYear() : now.getFullYear() + 1;
        return monthStd + ' ' + year;
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // Format dates
            let formattedCancelledDate = null;
            if (values.cancelled_at) {
                const date = new Date(values.cancelled_at);
                formattedCancelledDate = date.toISOString().split('T')[0];
            }
            // Determine states from renewal_choice
            const isLeaving = values.renewal_choice && values.renewal_choice.startsWith('LEAVING');
            const isM2M = values.renewal_choice === 'REMAIN MONTH 2 MONTH';

            // Auto-derive cancellation_end_month from renewal_choice
            const endMonth = isLeaving ? deriveEndMonth(values.renewal_choice) : null;

            // Update booking (renewal_choice_date is auto-set to now() by backend)
            await axios.patch(
                `${process.env.REACT_APP_API_URL}bookings/${current_booking.id}`,
                {
                    renewal_choice: values.renewal_choice || null,
                    cancelled: isLeaving ? values.cancelled : 0,
                    cancelled_at: isLeaving ? formattedCancelledDate : null,
                    cancelled_signed_at: isLeaving ? (values.cancelled_signed_at || null) : null,
                    cancellation_type: (isLeaving && values.cancelled) ? (values.cancellation_type || null) : null,
                    cancellation_end_month: endMonth,
                    late_notice: (isLeaving && values.cancelled) ? values.late_notice : 0,
                }
            );

            // Auto-set month_to_month on rates table based on renewal_choice
            if (current_booking.rates && current_booking.rates.length > 0) {
                const latestRate = current_booking.rates[current_booking.rates.length - 1];
                if (latestRate.id) {
                    await axios.patch(
                        `${process.env.REACT_APP_API_URL}rates/${latestRate.id}`,
                        {
                            month_to_month: isM2M ? 1 : 0,
                        }
                    );
                }
            }

            toast(<NotificationToast title="Success" message="Details updated" />);
            dispatch(getBooking(current_booking.id));
            fetchHistory();
        } catch (error) {
            console.error("Failed to update:", error);
            toast(<NotificationToast title="Error" message="Failed to update details" type="error" />);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteHistory = async (historyId) => {
        if (!window.confirm('Are you sure you want to delete this history entry?')) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}monthend/history/${historyId}`);
            toast(<NotificationToast title="Success" message="History entry deleted" />);
            fetchHistory();
        } catch (error) {
            console.error("Failed to delete history:", error);
            toast(<NotificationToast title="Error" message="Failed to delete history entry" type="error" />);
        }
    };

    const isSuperUser = current_staff?.isSuper == 1;

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'yes_renew': return 'bg-green-100 text-green-800';
            case 'm2m': return 'bg-blue-100 text-blue-800';
            case 'leaving': return 'bg-red-100 text-red-800';
            case 'revoke_cancellation': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'yes_renew': return 'Yes, Renew';
            case 'm2m': return 'Month 2 Month';
            case 'leaving': return 'Leaving';
            case 'revoke_cancellation': return 'Revoked Cancellation';
            default: return status;
        }
    };

    const getSourceBadge = (source) => {
        switch (source) {
            case 'whatsapp': return 'bg-green-50 text-green-700';
            case 'manual': return 'bg-purple-50 text-purple-700';
            case 'backfill': return 'bg-orange-50 text-orange-700';
            case 'system': return 'bg-gray-50 text-gray-700';
            default: return 'bg-gray-50 text-gray-700';
        }
    };

    const handleAddHistory = async () => {
        if (!newHistoryChoice) {
            toast(<NotificationToast title="Error" message="Please select a renewal choice" type="error" />);
            return;
        }
        setAddingHistory(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}monthend/history/${current_booking.id}`,
                {
                    renewal_choice: newHistoryChoice,
                    decided_at: newHistoryDate || null,
                    notes: newHistoryNotes || null,
                }
            );
            toast(<NotificationToast title="Success" message="Renewal history entry added" />);
            setNewHistoryChoice("");
            setNewHistoryDate("");
            setNewHistoryNotes("");
            setShowAddHistory(false);
            fetchHistory();
            dispatch(getBooking(current_booking.id));
        } catch (error) {
            console.error("Failed to add history:", error);
            toast(<NotificationToast title="Error" message="Failed to add history entry" type="error" />);
        } finally {
            setAddingHistory(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                                        Cancellations and Renewals
                                    </Dialog.Title>
                                    <div className="flex items-center gap-3">
                                        {onOpenReport && (
                                            <button
                                                type="button"
                                                onClick={onOpenReport}
                                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faChartLine} className="mr-1.5" />
                                                View Report
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            onClick={onClose}
                                        >
                                            <span className="sr-only">Close</span>
                                            <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="px-6 py-6 max-h-[80vh] overflow-y-auto">
                                    {loadingHistory ? (
                                        <div className="flex justify-center items-center py-12">
                                            <FontAwesomeIcon icon={faSpinnerThird} className="text-4xl text-indigo-600 animate-spin" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* Cancellation Form */}
                                            <Formik
                                                enableReinitialize={true}
                                                initialValues={{
                                                    renewal_choice: current_booking?.renewal_choice || "",
                                                    cancelled: current_booking?.cancelled || 0,
                                                    cancelled_at: current_booking?.cancelled_at || "",
                                                    cancelled_signed: current_booking?.cancelled_signed_at ? 1 : 0,
                                                    cancelled_signed_at: current_booking?.cancelled_signed_at || "",
                                                    cancellation_type: current_booking?.cancellation_type || "",
                                                    late_notice: current_booking?.late_notice || 0,
                                                }}
                                                onSubmit={handleSubmit}
                                            >
                                        {({ values, setFieldValue }) => {
                                            const isLeaving = values.renewal_choice && values.renewal_choice.startsWith('LEAVING');

                                            return (
                                            <Form>
                                                <div className="space-y-4 mb-6">
                                                    {/* Row 1: Renewal Choice + Save */}
                                                    <div className="grid grid-cols-3 gap-6">
                                                        <div>
                                                            <SettingsSelect
                                                                label="Renewal Choice"
                                                                name="renewal_choice"
                                                                onChange={setFieldValue}
                                                                value={values.renewal_choice}
                                                                options={[
                                                                    { value: "", label: "-- None --" },
                                                                    ...renewalChoiceOptions,
                                                                ]}
                                                            />
                                                            {showFieldInfo && <p className="mt-1 text-xs text-gray-400">Set by WhatsApp button reply or manually. Choice date auto-set to now on save.</p>}
                                                        </div>
                                                        <div className="flex items-end gap-3">
                                                            <SubmitButton loading={loading} text={loading ? "Saving..." : "Save Details"} />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowFieldInfo(!showFieldInfo)}
                                                                className="mb-1 text-xs text-gray-400 hover:text-gray-600 underline"
                                                            >
                                                                {showFieldInfo ? 'hide info' : 'info'}
                                                            </button>
                                                        </div>
                                                        <div></div>
                                                    </div>

                                                    {/* Row 2: Cancellation fields - Only shown when LEAVING */}
                                                    {isLeaving && (
                                                        <div className="grid grid-cols-4 gap-6 border-t pt-4">
                                                            {/* Cancelled toggle + date */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Cancelled</label>
                                                                <div className="flex items-center gap-3">
                                                                    <Field name="cancelled" as={SettingsSwitch} label="" description=" " />
                                                                    <Field name="cancelled_at" type="text" as={QuickFormDate} label="" />
                                                                </div>
                                                                {showFieldInfo && <p className="mt-1 text-xs text-gray-400">Auto-set when LEAVING is selected. Used for cancellation stats.</p>}
                                                            </div>

                                                            {/* Cancel Signed toggle + date */}
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Cancel Signed</label>
                                                                <div className="flex items-center gap-3">
                                                                    <Field name="cancelled_signed" as={SettingsSwitch} label="" description=" " />
                                                                    <Field name="cancelled_signed_at" type="text" as={QuickFormDate} label="" />
                                                                </div>
                                                                {showFieldInfo && <p className="mt-1 text-xs text-gray-400">Date tenant signed the cancellation document. Used on PDF and exports.</p>}
                                                            </div>

                                                            {/* Type - only when cancelled is on */}
                                                            <div>
                                                                {values.cancelled ? (
                                                                    <>
                                                                        <SettingsSelect
                                                                            label="Type"
                                                                            name="cancellation_type"
                                                                            onChange={setFieldValue}
                                                                            value={values.cancellation_type}
                                                                            options={cancellationTypeOptions}
                                                                        />
                                                                        {showFieldInfo && <p className="mt-1 text-xs text-gray-400">Early termination, abscond, or eviction. Drives cancellation type stats.</p>}
                                                                    </>
                                                                ) : null}
                                                            </div>

                                                            {/* Flag Late Notice - only when cancelled is on */}
                                                            <div>
                                                                {values.cancelled ? (
                                                                    <>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Flag Late Notice</label>
                                                                        <div className="w-fit">
                                                                            <Field name="late_notice" as={SettingsSwitch} label="" description=" " />
                                                                        </div>
                                                                        {showFieldInfo && <p className="mt-1 text-xs text-gray-400">Flag this cancellation as late notice.</p>}
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Form>
                                            );
                                        }}
                                    </Formik>

                                    {/* Renewal History Table */}
                                    <div className="mt-8 border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                                <FontAwesomeIcon icon={faHistory} className="mr-2 text-gray-500" />
                                                Renewal History
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                {loadingHistory && (
                                                    <FontAwesomeIcon icon={faSpinnerThird} className="text-indigo-600 animate-spin" />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddHistory(!showAddHistory)}
                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                                                    Add Entry
                                                </button>
                                            </div>
                                        </div>

                                        {/* Add History Form */}
                                        {showAddHistory && (
                                            <div className="mb-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                                                <div className="grid grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Choice</label>
                                                        <select
                                                            value={newHistoryChoice}
                                                            onChange={(e) => setNewHistoryChoice(e.target.value)}
                                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                        >
                                                            <option value="">Select...</option>
                                                            {renewalChoiceOptions.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                                                        <input
                                                            type="date"
                                                            value={newHistoryDate}
                                                            onChange={(e) => setNewHistoryDate(e.target.value)}
                                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                                        <input
                                                            type="text"
                                                            value={newHistoryNotes}
                                                            onChange={(e) => setNewHistoryNotes(e.target.value)}
                                                            placeholder="Optional notes"
                                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                        />
                                                    </div>
                                                    <div className="flex items-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleAddHistory}
                                                            disabled={addingHistory}
                                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                                                        >
                                                            {addingHistory ? "Saving..." : "Save"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddHistory(false)}
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {history.length === 0 && !loadingHistory ? (
                                            <p className="text-sm text-gray-500 italic text-center py-8">No renewal history yet</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {history.map((entry) => (
                                                    <div key={entry.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3 flex-wrap">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(entry.renewal_status)}`}>
                                                                    {getStatusLabel(entry.renewal_status)}
                                                                </span>
                                                                <span className="text-sm font-medium text-gray-900">{entry.renewal_choice}</span>
                                                                {entry.cancellation_type && (
                                                                    <span className="text-xs text-gray-500">• {entry.cancellation_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                                                )}
                                                                {entry.cancellation_end_month && (
                                                                    <span className="text-xs text-gray-500">• End: {entry.cancellation_end_month}</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSourceBadge(entry.source)}`}>
                                                                    {entry.source || 'system'}
                                                                </span>
                                                                {isSuperUser && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteHistory(entry.id)}
                                                                        className="text-gray-300 hover:text-red-500 transition-colors"
                                                                        title="Delete history entry"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                                                            <span>
                                                                {new Date(entry.decided_at).toLocaleDateString('en-US', {
                                                                    month: 'short', day: 'numeric', year: 'numeric',
                                                                    hour: '2-digit', minute: '2-digit'
                                                                })}
                                                            </span>
                                                            <span>
                                                                {entry.actioned_by && typeof entry.actioned_by === 'object'
                                                                    ? `by ${entry.actioned_by.name || ''} ${entry.actioned_by.surname || ''}`.trim()
                                                                    : (entry.source === 'whatsapp' ? 'by Client' : '')
                                                                }
                                                            </span>
                                                        </div>
                                                        {entry.notes && (
                                                            <p className="mt-2 text-sm text-gray-600">{entry.notes}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                        </>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
